import React, { Key } from 'react';
import { Button } from 'is24-corecss';
import { FormattedMessage } from 'react-intl';

import { getStatus, TeamMember } from '../../../../models/TeamMember';
import LifecycleHandlerButton from '../lifecycleHandlerButton/LifecycleHandlerButton';
import SortHeader from './SortHeader';
import { TeamMemberLifecycleCommand } from '../../../../models/TeamMemberLifecycleEvent';

import styles from './styles.module.css';
import pencilIcon from '../table/pencil.png';
import shareLinkIcon from '../table/shareLink.png';

type TableProps = {
  rows: TeamMember[] | null;
  onDeleteRow: (row: TeamMember) => void;
  onEditRow: (row: TeamMember) => void;
  onInvite: (row: TeamMember) => void;
  onRevokeInvitation: (row: TeamMember) => void;
  onSort: (column: string) => void;
  sortOrder: string;
  sortColumn: string;
  currentPageNr: number;
  pageSize: number;
  onCopyInvitationLink: (row: TeamMember) => void;
};

const getTransitions = (row: TeamMember) => {
  return row['lifecycleSnapshot']?.possibleTransitions?.map((t) => Object.keys(t)[0]) || [];
};

const hasTransition = (row: TeamMember, transition: TeamMemberLifecycleCommand): boolean => {
  return getTransitions(row).includes(transition);
};

const Table: (props: TableProps) => JSX.Element | null = ({
  rows,
  onDeleteRow,
  onEditRow,
  onInvite,
  onRevokeInvitation,
  onSort,
  sortColumn,
  sortOrder,
  currentPageNr,
  pageSize,
  onCopyInvitationLink,
}: TableProps) => {
  const handleSortClick = (column: string) => () => {
    onSort(column);
  };

  const handleOptionClick = (row: TeamMember, callback: (row: TeamMember) => void) => () => {
    if (row.id != null) {
      callback(row);
    }
  };

  return (
    rows && (
      <table data-testid='team-members-table' id='team-members-table' className={styles.teamMembersTable}>
        <thead className='is24-sort-header palm-hide'>
          <tr>
            <th key={'rowIndex' as Key} className={styles.headerCell}>
              <span className={styles.nonSortableHeader}>#.</span>
            </th>
            <th key={'email' as Key} className={styles.headerCell}>
              <SortHeader
                columnNameCode='teammanagement.table.header.email'
                sortOrder={sortOrder}
                onSort={handleSortClick('email')}
                isSorted={sortColumn === 'email'}
              />
            </th>
            <th key={'username' as Key} className={styles.headerCell}>
              <SortHeader
                columnNameCode='teammanagement.table.header.username'
                sortOrder={sortOrder}
                onSort={handleSortClick('username')}
                isSorted={sortColumn === 'username'}
              />
            </th>
            <th key={'name' as Key} className={styles.headerCell}>
              <SortHeader
                columnNameCode='teammanagement.table.header.name'
                sortOrder={sortOrder}
                onSort={handleSortClick('firstName,lastName')}
                isSorted={sortColumn === 'firstName,lastName'}
              />
            </th>
            <th key={'status' as Key} className={styles.headerCell}>
              <SortHeader
                columnNameCode='teammanagement.table.header.status'
                sortOrder={sortOrder}
                onSort={handleSortClick('status')}
                isSorted={sortColumn === 'status'}
              />
            </th>
            <th key={'note' as Key} className={styles.headerCell}>
              <span className={styles.nonSortableHeader}>
                <FormattedMessage id='teammanagement.table.header.note' />
              </span>
            </th>
            <th className={styles.headerCell}>
              <span className={styles.nonSortableHeader}>
                <FormattedMessage id='teammanagement.table.header.options' />
              </span>
            </th>
            <th key={'modifiedAt' as Key} className={styles.headerCell}>
              <SortHeader
                columnNameCode='teammanagement.table.header.modifiedAt'
                sortOrder={sortOrder}
                onSort={handleSortClick('modifiedAt')}
                isSorted={sortColumn === 'modifiedAt'}
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row: TeamMember, rowIndex: number) => (
            <tr key={row.id} className={`${styles.row} ${row.status === 'DEACTIVATED' ? styles.deactivated : ''}`}>
              <td key={'rowIndex'} className={styles.cell}>
                <span className={`font-italic ${styles.cellDescription}`}>{rowIndex + 1 + currentPageNr * pageSize}.</span>
              </td>
              <td key={'email'} className={styles.cell}>
                <span className={`desk-hide lap-hide font-italic ${styles.cellDescription}`}>
                  <FormattedMessage id='teammanagement.table.header.email' />
                  :&nbsp;
                </span>
                {row['email']}
              </td>
              <td key={'username'} className={styles.cell}>
                <span className={`desk-hide lap-hide font-italic ${styles.cellDescription}`}>
                  <FormattedMessage id='teammanagement.table.header.username' />
                  :&nbsp;
                </span>
                {row['username']}
              </td>
              <td key={'name'} className={styles.cell}>
                <span className={`desk-hide lap-hide font-italic ${styles.cellDescription}`}>
                  <FormattedMessage id='teammanagement.table.header.name' />
                  :&nbsp;
                </span>
                {`${row['firstName']} ${row['lastName']}`}
              </td>
              <td key={'status'} className={styles.cell}>
                <span className={`desk-hide lap-hide font-italic ${styles.cellDescription}`}>
                  <FormattedMessage id='teammanagement.table.header.status' />
                  :&nbsp;
                </span>
                {getStatus(row)}
              </td>
              <td key={'note'} className={styles.cell}>
                <span className={`desk-hide lap-hide font-italic ${styles.cellDescription}`}>
                  <FormattedMessage id='teammanagement.table.header.note' />
                  :&nbsp;
                </span>
                {row['note']}
              </td>
              <td className={styles.optionsCell}>
                {hasTransition(row, 'invite') && (
                  <LifecycleHandlerButton key='invite' teamMember={row} onClick={handleOptionClick(row, onInvite)} command='invite' />
                )}
                {['INVITED', 'INVITATION_ACCEPTED'].includes(row.status!) && (
                  <Button className={styles.actionLink} data-testid='copy-invitation-link' onClick={handleOptionClick(row, onCopyInvitationLink)}>
                    <img className={styles.optionIcon} src={shareLinkIcon} alt='share' />
                    <FormattedMessage id='teammanagement.table.button.copy.invitation.link' />
                  </Button>
                )}
                {hasTransition(row, 'revokeInvitation') && (
                  <LifecycleHandlerButton
                    key='revokeInvitation'
                    teamMember={row}
                    onClick={handleOptionClick(row, onRevokeInvitation)}
                    command='revokeInvitation'
                  />
                )}
                <Button className={styles.actionLink} data-testid='update-team-member' onClick={handleOptionClick(row, onEditRow)}>
                  <img className={styles.optionIcon} src={pencilIcon} alt='pen' />
                  <FormattedMessage id='teammanagement.table.button.edit' />
                </Button>
                {hasTransition(row, 'delete') && (
                  <LifecycleHandlerButton key='delete' teamMember={row} onClick={handleOptionClick(row, onDeleteRow)} command='delete' />
                )}
              </td>
              <td key={'modifiedAt'} className={styles.cell}>
                <span className={`desk-hide lap-hide font-italic ${styles.cellDescription}`}>
                  <FormattedMessage id='teammanagement.table.header.modifiedAt' />
                  :&nbsp;
                </span>
                <span data-testid='table-row-modifiedAt'>
                  {row['modifiedAt'] &&
                    new Date(row['modifiedAt'])
                      .toLocaleDateString('de-DE', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })
                      .replace(',', '')}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )
  );
};

export default Table;
